import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useLoad } from '../../hooks/useLoad';
import AuthImage from '../../components/auth/AuthImage';
import AuthContainer from '../../components/auth/AuthContainer';

function Signin(): JSX.Element {
  const auth = useAuth();
  const [data, setData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility

  // Toast for status messages
  const { setToast } = useToast();
  // Loading spinner
  const { setIsLoading } = useLoad();
  const navigate = useNavigate();

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const { name, value } = e.target;

    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleSignin(): Promise<void> {
    if (data.email === '' || data.password === '') {
      setToast({
        show: true,
        message: 'Please fill in all fields',
        type: 'error',
      });
      return;
    }
    setIsLoading(true);

    try {
      const result = await auth.signIn(data.email, data.password);
      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        const lowerCaseEmail = data.email.toLowerCase();
        setIsLoading(false);
        navigate(`/change-password?email=${lowerCaseEmail}`);
      } else {
        setIsLoading(false);
        navigate('/');
      }
    } catch (error: any) {
      console.log('error signing in', error);
      setIsLoading(false);
      setToast({
        show: true,
        message: error.message,
        type: 'error',
      });
    }
  }

  return (
    <main className="bg-white dark:bg-slate-900">
      {/* Content */}
      <AuthContainer>
        <h1 className="text-3xl text-slate-800 dark:text-slate-100 font-bold mb-6">
          Welcome back!
        </h1>
        {/* Form */}
        <div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                className="form-input w-full"
                type="email"
                name="email"
                onChange={handleChange}
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium mb-1"
                htmlFor="password"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  className="form-input w-full pr-10"
                  type={showPassword ? 'text' : 'password'} // Toggle password visibility
                  name="password"
                  autoComplete="on"
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)} // Toggle state
                  className="absolute inset-y-0 right-2 flex items-center text-sm text-slate-500"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-6">
            <div className="mr-1">
              <Link
                className="text-sm underline hover:no-underline"
                to="/forgot-password"
              >
                Forgot Password?
              </Link>
            </div>
            <button
              className="btn bg-indigo-500 hover:bg-indigo-600 text-white ml-3"
              onClick={() => {
                handleSignin().catch((error) => {
                  console.error('Error signing in', error);
                  setToast({
                    show: true,
                    message: error.message,
                    type: 'error',
                  });
                });
              }}
            >
              Sign In
            </button>
          </div>
        </div>
      </AuthContainer>
      {/* Image */}
      <AuthImage />
    </main>
  );
}

export default Signin;
